/**
 * StatCard — KPI stat card component.
 *
 * Layout adapts based on sparklinePosition (right | left | top | bottom).
 * - right/left: horizontal layout with sparkline beside value block
 * - top/bottom: vertical layout with sparkline above/below value block
 *
 * Colors: green = positive, red = negative, gray = neutral.
 *
 * Sparkline respects typeStyle properties: showSparkline, sparklinePosition,
 * lineStyle, lineWidth, areaOpacity, showMarkers, markerSize.
 */
import React, { useEffect, useRef, useMemo } from 'react';
import { tokens } from '@fluentui/react-components';
import * as echarts from 'echarts/core';
import { LineChart } from 'echarts/charts';
import { GridComponent } from 'echarts/components';
import { SVGRenderer } from 'echarts/renderers';
import type { ChartData, TrendDataPoint } from '../../../api/types';
import type { TypeStyle } from '../../edit/visualConfig/types';
import { formatCompact } from '../../../utils/formatters';

// Register minimal ECharts modules for sparkline
echarts.use([LineChart, GridComponent, SVGRenderer]);

// ── Types ───────────────────────────────────────────────────────────────────

export interface StatCardProps {
  data: ChartData;
  title?: string;
  trend?: TrendDataPoint[] | null;
  /** Sparkline style from chart editor (lineWidth, lineStyle, etc.) */
  typeStyle?: TypeStyle;
  /** Primary palette color for sparkline (resolved from dashboard/chart palette) */
  paletteColor?: string;
}

// ── Helpers ─────────────────────────────────────────────────────────────────

const COLOR_POSITIVE = '#10b981'; // green-500
const COLOR_NEGATIVE = '#ef4444'; // red-500
const COLOR_NEUTRAL = '#9ca3af'; // gray-400

function computeChange(current: number | null, previous: number | null) {
  if (current == null || previous == null || previous === 0) {
    return null;
  }
  return ((current - previous) / Math.abs(previous)) * 100;
}

function getChangeColor(pct: number | null): string {
  if (pct == null || pct === 0) return COLOR_NEUTRAL;
  return pct > 0 ? COLOR_POSITIVE : COLOR_NEGATIVE;
}

function getChangeArrow(pct: number | null): string {
  if (pct == null || pct === 0) return '';
  return pct > 0 ? '▲' : '▼';
}

/** Convert a hex or CSS color to rgba with given alpha. Falls back to the raw color. */
function hexToRgba(hex: string, alpha: number): string {
  const match = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!match) return hex; // not a simple hex — return as-is
  const r = parseInt(match[1], 16);
  const g = parseInt(match[2], 16);
  const b = parseInt(match[3], 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

// ── Sparkline ───────────────────────────────────────────────────────────────

type SparklinePosition = 'bottom' | 'top' | 'left' | 'right';

interface SparklineProps {
  trend: TrendDataPoint[];
  typeStyle?: TypeStyle;
  /** Primary color for sparkline line/area */
  color?: string;
  /** Layout position — determines dimensions */
  position?: SparklinePosition;
}

const Sparkline: React.FC<SparklineProps> = React.memo(({ trend, typeStyle, color, position = 'right' }) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const instanceRef = useRef<echarts.ECharts | null>(null);

  // Extract style props with defaults
  const lineWidth = typeStyle?.lineWidth ?? 2;
  const lineStyle = typeStyle?.lineStyle ?? 'smooth';
  const areaOpacity = typeStyle?.areaOpacity ?? 0.2;
  const showMarkers = typeStyle?.showMarkers ?? false;
  const markerSize = typeStyle?.markerSize ?? 3;

  const lineColor = color || tokens.colorBrandForeground1;

  const isHorizontal = position === 'left' || position === 'right';

  // Mount/unmount: create and dispose ECharts instance once
  useEffect(() => {
    if (!chartRef.current) return;
    instanceRef.current = echarts.init(chartRef.current, undefined, { renderer: 'svg' });

    const ro = new ResizeObserver(() => instanceRef.current?.resize());
    ro.observe(chartRef.current);

    return () => {
      ro.disconnect();
      instanceRef.current?.dispose();
      instanceRef.current = null;
    };
  }, []);

  // Update chart options when data or style props change
  useEffect(() => {
    if (!instanceRef.current) return;

    const smooth = lineStyle === 'smooth' ? true : lineStyle === 'step' ? false : false;
    const step = lineStyle === 'step' ? 'middle' as const : false;

    instanceRef.current.setOption({
      grid: { left: 0, right: 0, top: 2, bottom: 2 },
      xAxis: { type: 'category', show: false, data: trend.map((d) => d.date) },
      yAxis: { type: 'value', show: false },
      series: [
        {
          type: 'line',
          data: trend.map((d) => d.value),
          smooth,
          step: step || undefined,
          symbol: showMarkers ? 'circle' : 'none',
          symbolSize: showMarkers ? markerSize : 0,
          lineStyle: { width: lineWidth, color: lineColor },
          itemStyle: showMarkers ? { color: lineColor } : undefined,
          areaStyle: areaOpacity > 0
            ? {
                color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                  { offset: 0, color: hexToRgba(lineColor, areaOpacity) },
                  { offset: 1, color: hexToRgba(lineColor, areaOpacity * 0.1) },
                ]),
              }
            : undefined,
        },
      ],
      tooltip: { show: false },
      animation: false,
    }, true);
  }, [trend, lineWidth, lineStyle, areaOpacity, showMarkers, markerSize, lineColor]);

  // Horizontal (left/right): fill available height, fixed width
  // Vertical (top/bottom): full width, fixed height
  const style: React.CSSProperties = isHorizontal
    ? { width: '100%', height: '72px' }
    : { width: '100%', height: '32px' };

  return <div ref={chartRef} style={style} />;
});

Sparkline.displayName = 'Sparkline';

// ── Main Component ──────────────────────────────────────────────────────────

const StatCard: React.FC<StatCardProps> = ({ data, title, trend, typeStyle, paletteColor }) => {
  // Extract values from unified chart data
  const { currentValue, previousValue } = useMemo(() => {
    if (!data?.rows?.length || !data?.columns?.length) {
      return { currentValue: null, previousValue: null };
    }

    const row = data.rows[0];
    const rawCv = row[0] != null ? Number(row[0]) : null;
    const rawPv = row.length > 1 && row[1] != null ? Number(row[1]) : null;
    const cv = rawCv != null && Number.isFinite(rawCv) ? rawCv : null;
    const pv = rawPv != null && Number.isFinite(rawPv) ? rawPv : null;

    return { currentValue: cv, previousValue: pv };
  }, [data]);

  const changePct = computeChange(currentValue, previousValue);
  const changeColor = getChangeColor(changePct);
  const arrow = getChangeArrow(changePct);

  // Comparison visibility
  const showComparison = typeStyle?.showComparison ?? true;

  // Sparkline visibility & position
  const showSparkline = typeStyle?.showSparkline ?? true;
  const sparklinePosition: SparklinePosition = typeStyle?.sparklinePosition ?? 'right';
  const hasTrend = showSparkline && trend && trend.length > 1;
  const isHorizontal = sparklinePosition === 'left' || sparklinePosition === 'right';

  // Format display value
  const displayValue =
    currentValue != null ? formatCompact(currentValue, 1) : '—';

  // ── Value block (title + value + change) ──

  const valueBlock = (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: isHorizontal && hasTrend ? 'flex-start' : 'center',
      gap: '2px',
      flex: isHorizontal && hasTrend ? '0 0 auto' : undefined,
      minWidth: 0,
    }}>
      {title && <div style={cardStyles.title}>{title}</div>}
      <div style={cardStyles.value}>{displayValue}</div>
      {showComparison && changePct != null && (
        <div style={{ ...cardStyles.change, color: changeColor }}>
          <span style={cardStyles.arrow}>{arrow}</span>
          <span style={cardStyles.changePct}>
            {Math.abs(changePct).toFixed(1)}%
          </span>
          <span style={cardStyles.changeLabel}>vs previous period</span>
        </div>
      )}
    </div>
  );

  // ── Sparkline block ──

  const sparklineBlock = hasTrend ? (
    <div style={{
      ...(isHorizontal
        ? { flex: '1 1 0', minWidth: '60px', maxWidth: '200px' }
        : { width: '100%', maxWidth: '200px', marginTop: '2px' }
      ),
    }}>
      <Sparkline trend={trend!} typeStyle={typeStyle} color={paletteColor} position={sparklinePosition} />
    </div>
  ) : null;

  // ── Container layout based on sparkline position ──

  if (isHorizontal && hasTrend) {
    // Horizontal layout: value block + sparkline side by side
    const isLeft = sparklinePosition === 'left';
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%',
        padding: '4px 12px',
        gap: '12px',
        overflow: 'hidden',
      }}>
        {isLeft ? sparklineBlock : valueBlock}
        {isLeft ? valueBlock : sparklineBlock}
      </div>
    );
  }

  // Vertical layout: top or bottom sparkline
  const isTop = sparklinePosition === 'top';
  return (
    <div style={cardStyles.container}>
      {isTop && sparklineBlock}
      {valueBlock}
      {!isTop && sparklineBlock}
    </div>
  );
};

// ── Styles ──────────────────────────────────────────────────────────────────

const cardStyles: Record<string, React.CSSProperties> = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    padding: '4px 12px',
    gap: '1px',
    overflow: 'hidden',
  },
  title: {
    fontSize: '12px',
    fontWeight: 500,
    color: tokens.colorNeutralForeground3,
    textAlign: 'center',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: '100%',
  },
  value: {
    fontSize: '26px',
    fontWeight: 700,
    lineHeight: 1.1,
    color: tokens.colorNeutralForeground1,
    textAlign: 'center',
    whiteSpace: 'nowrap',
  },
  change: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: '13px',
    fontWeight: 500,
  },
  arrow: {
    fontSize: '10px',
  },
  changePct: {
    fontWeight: 600,
  },
  changeLabel: {
    fontWeight: 400,
    color: tokens.colorNeutralForeground3,
    fontSize: '12px',
  },
};

export default StatCard;
